import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import ChevronUpIcon from "@atlaskit/icon/core/chevron-up";
import LinkExternalIcon from "@atlaskit/icon/core/link-external";
import OfficeBuildingIcon from "@atlaskit/icon/core/office-building";
import PersonIcon from "@atlaskit/icon/core/person";
import ScreenIcon from "@atlaskit/icon/core/screen";
import DatabaseStorageCacheIcon from "@atlaskit/icon-lab/core/database-storage-cache";
import {token} from "@atlaskit/tokens";
import {useState, type ReactNode} from "react";
import {AnankeText} from "@vesta/ananke/components/anankeText";
import type {LogCardData} from "~/modules/activityTimeline/types/activityTimeline.types";
import {formatTime12Hour} from "~/utilities/utilities";

const TIMELINE_ROW_GAP = "var(--ds-space-150, 12px)";

/** Opacity for activity cards tied to inactive lifecycles/journeys (customer profile). */
export const ACTIVITY_LOG_CARD_INACTIVE_OPACITY = 0.45;

function ActivityHeaderPill({icon, label}: {icon: ReactNode; label: string}) {
    return (
        <span
            className="inline-flex max-w-full items-center gap-ds-space-050 rounded-[3px] border px-ds-space-075 py-ds-space-025"
            style={{
                borderColor: token("color.border"),
                background: token("elevation.surface"),
            }}
        >
            <span className="inline-flex shrink-0 items-center">{icon}</span>
            <AnankeText size="small" color="color.text.subtle">
                {label}
            </AnankeText>
        </span>
    );
}

function ActivityStatusPill({status}: {status: string}) {
    return (
        <span
            className="inline-flex w-max shrink-0 items-center rounded-[3px] border px-ds-space-075 py-ds-space-025 text-ds-font-body-small"
            style={{
                borderColor: token("color.border.accent.blue"),
                background: token("color.background.accent.blue.subtlest"),
                color: token("color.text.information"),
            }}
        >
            {status}
        </span>
    );
}

function ActivityUtmSourcePill({source}: {source: string}) {
    return (
        <ActivityHeaderPill
            icon={<DatabaseStorageCacheIcon label="" size="small" color={token("color.icon.subtle")} />}
            label={source}
        />
    );
}

function ActivityJourneyPill({label}: {label: string}) {
    return (
        <ActivityHeaderPill
            icon={<LinkExternalIcon label="" size="small" color={token("color.icon.subtle")} />}
            label={label}
        />
    );
}

function ActivityStrikethroughValue({children}: {children: ReactNode}) {
    return (
        <span
            style={{
                textDecoration: "line-through",
                color: token("color.text.subtlest"),
                whiteSpace: "pre-line",
            }}
        >
            {children}
        </span>
    );
}

function ActivityFieldChangeLine({line}: {line: {fieldLabel: string; oldDisplay: string; newDisplay: string}}) {
    const hasOld = line.oldDisplay !== "-" && line.oldDisplay.trim() !== "";
    const hasNew = line.newDisplay !== "-" && line.newDisplay.trim() !== "";
    const multilineValueStyle = {whiteSpace: "pre-line" as const};

    if (hasOld === false && hasNew === false) {
        return null;
    }

    return (
        <AnankeText color="color.text">
            {line.fieldLabel}
            {hasOld || hasNew ? " " : ""}
            {hasOld ? (
                <>
                    <span
                        style={{
                            textDecoration: "line-through",
                            color: token("color.text.subtlest"),
                            ...multilineValueStyle,
                        }}
                    >
                        {line.oldDisplay}
                    </span>
                    {hasNew ? " - " : ""}
                </>
            ) : null}
            {hasNew ? <span style={multilineValueStyle}>{line.newDisplay}</span> : null}
        </AnankeText>
    );
}

function ActivityOldNewContextLine({
    prefix,
    oldValue,
    newValue,
}: {
    prefix?: string;
    oldValue: string;
    newValue: string;
}) {
    return (
        <AnankeText color="color.text">
            {prefix}
            <ActivityStrikethroughValue>{oldValue}</ActivityStrikethroughValue>
            {" - "}
            <span style={{whiteSpace: "pre-line"}}>{newValue}</span>
        </AnankeText>
    );
}

function ActivityLogCardContext({data}: {data: LogCardData}) {
    if (data.contextFieldChanges !== undefined && data.contextFieldChanges.length > 0) {
        return (
            <div className="grid gap-ds-space-050">
                {data.contextFieldChanges.map((line, index) => (
                    <ActivityFieldChangeLine key={`${line.fieldLabel}-${index}`} line={line} />
                ))}
            </div>
        );
    }

    const oldValue = data.contextOldValue;
    const newValue = data.contextNewValue;

    if (oldValue !== undefined && newValue !== undefined && oldValue !== "" && newValue !== "") {
        return <ActivityOldNewContextLine prefix={data.contextPrefix} oldValue={oldValue} newValue={newValue} />;
    }

    if (data.context.trim() !== "") {
        return (
            <AnankeText color="color.text">
                <span style={{whiteSpace: "pre-line"}}>{data.context}</span>
            </AnankeText>
        );
    }

    return null;
}

function ActivityLogCardFooter({data}: {data: LogCardData}) {
    const isUser = data.createdBy.kind === "user";

    return (
        <div className="flex min-w-0 flex-wrap items-center gap-x-ds-space-075 gap-y-ds-space-050">
            <span className="inline-flex min-w-0 items-center gap-ds-space-050">
                <span className="inline-flex shrink-0 items-center [&_svg]:h-4 [&_svg]:w-4">
                    {isUser ? (
                        <PersonIcon label="" size="small" color={token("color.icon.subtle")} />
                    ) : (
                        <ScreenIcon label="" size="small" color={token("color.icon.subtle")} />
                    )}
                </span>
                <AnankeText size="small" color="color.text.subtlest">
                    {data.createdBy.displayName}
                </AnankeText>
            </span>
            {data.ou !== undefined && data.ou !== "" ? (
                <>
                    <AnankeText size="small" color="color.text.subtlest">
                        •
                    </AnankeText>
                    <span className="inline-flex min-w-0 items-center gap-ds-space-050">
                        <span className="inline-flex shrink-0 items-center [&_svg]:h-4 [&_svg]:w-4">
                            <OfficeBuildingIcon label="" size="small" color={token("color.icon.subtle")} />
                        </span>
                        <AnankeText size="small" color="color.text.subtlest">
                            {data.ou}
                        </AnankeText>
                    </span>
                </>
            ) : null}
        </div>
    );
}

export function ActivityLogCardShell({data}: {data: LogCardData}) {
    const headerPills = [
        data.journeyLabel !== undefined && data.journeyLabel.trim() !== "" ? (
            <ActivityJourneyPill key="journey" label={data.journeyLabel} />
        ) : null,
        data.source !== undefined && data.source.trim() !== "" ? (
            <ActivityUtmSourcePill key="source" source={data.source} />
        ) : null,
    ].filter((pill) => pill !== null);

    return (
        <div
            className="grid w-full min-w-0 gap-ds-space-150 rounded-ds-border-radius border border-ds-border p-ds-space-150"
            style={{background: token("elevation.surface")}}
        >
            <div className="grid grid-cols-[minmax(0,1fr)_max-content] items-start gap-ds-space-100">
                <div className="flex min-w-0 flex-wrap items-center gap-x-ds-space-075 gap-y-ds-space-050">
                    <AnankeText size="large" weight="medium">
                        {data.entityAction}
                    </AnankeText>
                    {data.status !== undefined && data.status !== "" ? (
                        <>
                            <AnankeText size="large" color="color.text.subtlest">
                                ·
                            </AnankeText>
                            <ActivityStatusPill status={data.status} />
                        </>
                    ) : null}
                </div>
                {headerPills.length > 0 ? (
                    <div className="flex max-w-full flex-wrap items-center justify-end gap-ds-space-050">
                        {headerPills}
                    </div>
                ) : null}
            </div>

            <div className="border-t border-ds-border" />

            <div className="min-w-0 break-words">
                <ActivityLogCardContext data={data} />
            </div>

            {data.remarks !== undefined && data.remarks.trim() !== "" ? (
                <div
                    className="rounded-ds-border-radius px-ds-space-100 py-ds-space-075"
                    style={{background: token("color.background.accent.gray.subtlest")}}
                >
                    <AnankeText size="small" color="color.text.subtle">
                        &quot;{data.remarks}&quot;
                    </AnankeText>
                </div>
            ) : null}

            <div className="border-t border-ds-border" />

            <ActivityLogCardFooter data={data} />
        </div>
    );
}

function TimelineRailSegment({isLast}: {isLast: boolean}) {
    return (
        <div
            className="min-h-[1rem] w-[2px] self-stretch"
            style={{
                background: token("color.border"),
                marginBottom: isLast ? undefined : `calc(-1 * ${TIMELINE_ROW_GAP})`,
                paddingBottom: isLast ? undefined : TIMELINE_ROW_GAP,
            }}
        />
    );
}

export function ActivityTimelineDaySection({
    label,
    items,
    defaultOpen = true,
}: {
    label: string;
    items: Array<LogCardData>;
    defaultOpen?: boolean;
}) {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="grid w-full min-w-0 gap-ds-space-150">
            <button
                type="button"
                className="flex w-full cursor-pointer items-center justify-between pb-ds-space-100 text-left"
                onClick={() => {
                    setIsOpen((previous) => !previous);
                }}
            >
                <AnankeText weight="medium">{label}</AnankeText>
                {isOpen ? (
                    <ChevronUpIcon label="" color={token("color.icon.subtle")} />
                ) : (
                    <ChevronDownIcon label="" color={token("color.icon.subtle")} />
                )}
            </button>
            {isOpen === true ? (
                <div className="grid w-full min-w-0 gap-y-ds-space-150">
                    {items.map((item, index) => {
                        const inactiveRowStyle =
                            item.isInactive === true
                                ? {opacity: ACTIVITY_LOG_CARD_INACTIVE_OPACITY}
                                : undefined;

                        return (
                        <div
                            key={item.id}
                            className="grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)] items-stretch gap-x-ds-space-100"
                            style={inactiveRowStyle}
                            aria-disabled={item.isInactive === true ? true : undefined}
                        >
                            <div className="grid w-max max-w-[4.75rem] shrink-0 grid-rows-[max-content_1fr] justify-items-center gap-y-ds-space-050">
                                <AnankeText size="small" color="color.text.subtlest">
                                    {formatTime12Hour(item.timestamp)}
                                </AnankeText>
                                <TimelineRailSegment isLast={index === items.length - 1} />
                            </div>
                            <ActivityLogCardShell data={item} />
                        </div>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}
